import { Box, Button, ButtonGroup, ToggleButton } from '@flixbus/honeycomb-react';
import { DataTable, createDataTableColumnHelper } from '@ridehub/ui-widgets';
import { Icon, IconArrowBigRight, IconArrowLeft, IconArrowRight } from '@flixbus/honeycomb-icons-react';
import { OrdersPBD, YieldTableCell, YieldTableLegend } from './ui-yield-table-cell';
import { PBD_BOUNDARIES, calculatePBD } from '@ridehub/util-temporal';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RideDetails, filterRelations, useFeatureFlags, useRide } from '@ridehub/data-ride';
import { useNavigate, useParams } from 'react-router-dom';

import { Temporal } from '@js-temporal/polyfill';
import styles from './feat-yield-table.module.scss';
import { useLocalization } from '@ridehub/feat-localization';

interface YieldTableRow {
    relationFrom: string;
    relationTo: string;
    relationFromStopCode: string;
    relationToStopCode: string;
    [key: `pbd${number}`]: OrdersPBD;
    totalTix: string;
    avgYield: string;
    isTotalRow?: boolean;
}
export interface FeatYieldTableProps {
    auth?: boolean;
}

export function FeatYieldTable(props: FeatYieldTableProps = {}) {
    const authenticateRequests = props.auth === false ? false : true;
    const { data: featureFlags, isPending: isPendingFeatureFlag } = useFeatureFlags(true, authenticateRequests);
    const { rideId, segmentId, relationId } = useParams();
    const navigate = useNavigate();
    const { data: rideData } = useRide(rideId, authenticateRequests);
    const [selectedPBDOffset, setSelectedPBDOffset] = useState(0);
    const isOffsetInitialized = useRef<boolean>(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const { localizeCurrency, localizeDistance, localizeUnit, printWithLocalUnit } = useLocalization();
    const maxRowsContractedView = 10;
    const [showAllColumns, setShowAllColumns] = useState<boolean>(false);

    function initializePBDwindowOffset(rideData?: RideDetails) {
        if (rideData && !isOffsetInitialized.current) {
            const firstSegment = rideData.segments[0];
            const { currentPBDBoundary } = calculatePBD(firstSegment.departure_time, firstSegment.departure_timezone);
            const colIdx = PBD_BOUNDARIES.findIndex((pbdValue) => pbdValue === currentPBDBoundary);
            if (colIdx >= 12) {
                setSelectedPBDOffset(12);
                isOffsetInitialized.current = true;
            }
        }
    }

    useEffect(() => initializePBDwindowOffset(rideData), [rideData]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 2000px)');

        // Set initial state
        setShowAllColumns(mediaQuery.matches);

        // Listen for viewport changes
        const handleViewportChange = (e: MediaQueryListEvent) => {
            setShowAllColumns(e.matches);
        };

        mediaQuery.addEventListener('change', handleViewportChange);

        return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }, []);

    function navigateToRelation(fromStop: string, toStop: string) {
        const destinationRelationId = `${fromStop}_${toStop}`;
        if (relationId !== destinationRelationId) {
            navigate(`/rides/${rideId}/relations/${destinationRelationId}`);
        }
    }

    const pbdToDateTime = useCallback(
        (pbd: number): Temporal.ZonedDateTime => {
            if (!rideData) {
                throw new Error('Call with non-existing ride data');
            }
            const rideDeparture = rideData.segments[0].departure_time;
            const days = Math.floor(pbd);
            const fractionalDays = pbd - days;
            const minutes = Math.round(fractionalDays * 24 * 60);
            return rideDeparture?.subtract(
                new Temporal.Duration(undefined, undefined, undefined, days, undefined, minutes)
            );
        },
        [rideData]
    );

    const yieldTableData: Array<YieldTableRow> = useMemo(() => {
        if (!rideData) return [];

        return filterRelations({ segmentId, relationId }, rideData)
            .map((el) => {
                const fromSegment = rideData.segments.find((seg) => seg.from_uuid === el.from_uuid);
                const toSegment = rideData.segments.find((seg) => seg.to_uuid === el.to_uuid);
                const YTRow: YieldTableRow = {
                    relationFrom: el.from_uuid,
                    relationTo: el.to_uuid,
                    relationFromStopCode: fromSegment?.from_stop_code || '',
                    relationToStopCode: toSegment?.to_stop_code || '',
                    totalTix: '0',
                    avgYield: '0',
                };
                PBD_BOUNDARIES.forEach((pbd) => {
                    YTRow[`pbd${pbd}`] = { orders: [], totalTix: '0' };
                });
                let totalRevenueEuro = 0;
                if (el.orders && el.orders.length > 0) {
                    el.orders.sort((a, b) => a.pre_booking_day - b.pre_booking_day);
                    let pbd_boundary_idx = PBD_BOUNDARIES.length - 1;
                    el.orders.forEach((order) => {
                        const localizedPrice = localizeCurrency(order.price_eur, 'country');
                        totalRevenueEuro += order.price_eur;
                        while (order.pre_booking_day >= PBD_BOUNDARIES[pbd_boundary_idx - 1]) {
                            pbd_boundary_idx -= 1;
                        }
                        const currPBD = PBD_BOUNDARIES[pbd_boundary_idx];
                        YTRow[`pbd${currPBD}`].orders.push({
                            bookingClass: order.booking_class,
                            orderDateTime: pbdToDateTime(order.pre_booking_day),
                            price: localizedPrice,
                        });
                        YTRow[`pbd${currPBD}`].totalTix = `${parseInt(YTRow[`pbd${currPBD}`].totalTix) + 1}`;
                        YTRow.totalTix = `${parseInt(YTRow.totalTix) + 1}`;
                    });
                }
                const localizedDistance = localizeDistance(el.distance_km);
                const localizedTotal = localizeCurrency(totalRevenueEuro, 'report');
                const avgRevenue = el.orders?.length ? localizedTotal / el.orders.length : 0;
                const relationYield = localizedDistance ? (avgRevenue / localizedDistance) * 100 : 0;
                YTRow.avgYield = printWithLocalUnit('revenuePerDistance', relationYield, true, 'report');
                return YTRow;
            })
            .filter((row) => parseInt(row.totalTix) > 0)
            .sort((a, b) => parseInt(b.totalTix) - parseInt(a.totalTix));
    }, [rideData, pbdToDateTime, segmentId, relationId, localizeCurrency, localizeDistance, printWithLocalUnit]);

    const createTotalRow = useCallback((data: YieldTableRow[], pbds: number[]): YieldTableRow => {
        const totalRow: YieldTableRow = {
            relationFrom: 'TOTAL',
            relationTo: 'TOTAL',
            relationFromStopCode: 'Total',
            relationToStopCode: '',
            totalTix: '0',
            avgYield: '',
            isTotalRow: true,
        };

        let grandTotal = 0;

        // Initialize PBD totals
        pbds.forEach((pbd) => {
            totalRow[`pbd${pbd}`] = { orders: [], totalTix: '0' };
        });

        // Sum across all data rows
        data.forEach((row) => {
            pbds.forEach((pbd) => {
                const currentTix = parseInt(row[`pbd${pbd}`]?.totalTix || '0');
                const existingTix = parseInt(totalRow[`pbd${pbd}`].totalTix);
                totalRow[`pbd${pbd}`].totalTix = `${existingTix + currentTix}`;
            });

            const rowTix = parseInt(row.totalTix);
            grandTotal += rowTix;
        });

        totalRow.totalTix = `${grandTotal}`;
        return totalRow;
    }, []);

    const selectedPBDs = useMemo(
        () => (showAllColumns ? PBD_BOUNDARIES : PBD_BOUNDARIES.slice(selectedPBDOffset, selectedPBDOffset + 13)),
        [selectedPBDOffset, showAllColumns]
    );

    const yieldTableColumns = useMemo(() => {
        const { accessor } = createDataTableColumnHelper<(typeof yieldTableData)[0]>();
        const columns = [
            accessor('relationFromStopCode', {
                header: 'PBD',
                size: -1,
                cell: ({ row }) => {
                    if (row.original.isTotalRow) {
                        return <strong className={styles.totalLabel}>{row.original.relationFromStopCode}</strong>;
                    }
                    return (
                        <Button
                            onClick={() => navigateToRelation(row.original.relationFrom, row.original.relationTo)}
                            appearance="link"
                        >
                            {row.original.relationFromStopCode}
                            <Icon InlineIcon={IconArrowBigRight} extraClasses={styles.routeArrow} />
                            {row.original.relationToStopCode}
                        </Button>
                    );
                },
            }),
        ];
        selectedPBDs.forEach((pbd) => {
            columns.push(
                accessor(`pbd${pbd}.totalTix`, {
                    header: `${pbd}`,
                    size: -1,
                    cell: ({ row }) => {
                        const data = row.original[`pbd${pbd}`];
                        if (row.original.isTotalRow) {
                            const total = parseInt(data?.totalTix || '0');
                            return total > 0 ? <span className={styles.totalCell}>{total}</span> : null;
                        }
                        return <YieldTableCell data={data} />;
                    },
                })
            );
        });
        columns.push(
            accessor('totalTix', {
                header: 'Tix',
                size: -1,
                cell: ({ row }) => {
                    if (row.original.isTotalRow) {
                        return <strong className={styles.totalCell}>{row.original.totalTix}</strong>;
                    }
                    return row.original.totalTix;
                },
            })
        );
        columns.push(
            accessor('avgYield', {
                header: `Avg Yield (${localizeUnit('revenuePerDistance', true, 'report')})`,
                size: -1,
                cell: ({ row }) => {
                    if (row.original.isTotalRow) {
                        return <span className={styles.totalCell}>{row.original.avgYield}</span>;
                    }
                    return row.original.avgYield;
                },
            })
        );
        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPBDs, localizeUnit]);

    const yieldTableDataView = useMemo(() => {
        const dataSlice = yieldTableData.slice(0, isExpanded ? undefined : maxRowsContractedView);
        const totalRow = createTotalRow(yieldTableData, selectedPBDs);
        return [totalRow, ...dataSlice];
    }, [yieldTableData, isExpanded, createTotalRow, selectedPBDs]);

    if (isPendingFeatureFlag || !featureFlags?.yield_table || !rideData) {
        return null;
    }
    if (yieldTableData.length === 0) {
        return (
            <Box extraClasses={styles.yieldTableGroup}>
                <h2>Yield Table</h2>
                <p>No bookings yet</p>
            </Box>
        );
    }

    return (
        <Box extraClasses={styles.yieldTableGroup}>
            <h2>Yield Table</h2>
            <ButtonGroup orientation="horizontal" className={styles.tableControlButtons}>
                {yieldTableData.length > maxRowsContractedView ? (
                    <ToggleButton
                        extraClasses={styles.expandButton}
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label="Toggle expand"
                        aria-pressed={isExpanded}
                    >
                        {isExpanded ? 'Collapse' : 'Expand'}
                    </ToggleButton>
                ) : (
                    <div className={styles.placeholder} />
                )}
                {!showAllColumns ? (
                    <div>
                        <Button
                            disabled={selectedPBDOffset === 0}
                            appearance="link"
                            aria-label="Shift PBD Window left"
                            onClick={() => setSelectedPBDOffset(0)}
                        >
                            <Icon InlineIcon={IconArrowLeft} aria-hidden="true" />
                        </Button>
                        <Button
                            disabled={selectedPBDOffset === 12}
                            appearance="link"
                            aria-label="Shift PBD Window right"
                            onClick={() => setSelectedPBDOffset(12)}
                        >
                            <Icon InlineIcon={IconArrowRight} aria-hidden="true" />
                        </Button>
                    </div>
                ) : (
                    <div />
                )}
            </ButtonGroup>
            <div data-wide-table={showAllColumns}>
                <DataTable scrollable={false} columns={yieldTableColumns} data={yieldTableDataView} />
            </div>
            <YieldTableLegend />
        </Box>
    );
}

export default FeatYieldTable;
